import { Controller } from "@hotwired/stimulus"

// Controls section visibility in month calendar view
// Listens for visibility change events from the filter panel
// Dynamically adjusts layout when sections are hidden
export default class extends Controller {
  static targets = [
    "appointmentsSection",
    "sickNotesSection",
    "leaveRequestsSection",
    "tasksSection",
    "weekContainer",
    "dayCell",
  ]

  static STORAGE_KEY = "plantafel_section_states"

  // Filterable sections (tasks are always visible)
  static FILTERABLE_SECTIONS = ["sickNotes", "leaveRequests", "appointments"]

  connect() {
    // Initialize visibility state
    this.visibilityState = this.loadVisibilityState()

    // Apply initial visibility state from localStorage
    this.applyVisibilityState()

    // Listen for visibility changes from filter panel
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this)
    this.boundHandleVisibilityLoaded = this.handleVisibilityLoaded.bind(this)
    window.addEventListener(
      "plantafel:section-visibility-changed",
      this.boundHandleVisibilityChange,
    )
    window.addEventListener("plantafel:section-visibility-loaded", this.boundHandleVisibilityLoaded)
  }

  disconnect() {
    window.removeEventListener(
      "plantafel:section-visibility-changed",
      this.boundHandleVisibilityChange,
    )
    window.removeEventListener(
      "plantafel:section-visibility-loaded",
      this.boundHandleVisibilityLoaded,
    )
  }

  // Load visibility state from localStorage
  loadVisibilityState() {
    try {
      const saved = localStorage.getItem(this.constructor.STORAGE_KEY)
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (_e) {
      // Ignore storage errors
    }
    // Default: all sections visible
    return {
      appointments: true,
      sickNotes: true,
      leaveRequests: true,
    }
  }

  // Apply visibility state to all sections
  applyVisibilityState() {
    const state = this.visibilityState
    this.applySectionVisibility("appointments", state.appointments !== false)
    this.applySectionVisibility("sickNotes", state.sickNotes !== false)
    this.applySectionVisibility("leaveRequests", state.leaveRequests !== false)

    // Recalculate layout after applying visibility
    this.recalculateLayout()
  }

  // Handle visibility change event from filter panel
  handleVisibilityChange(event) {
    const { section, visible } = event.detail
    this.visibilityState[section] = visible
    this.applySectionVisibility(section, visible)
    this.recalculateLayout()
  }

  // Handle initial visibility state loaded from filter panel
  handleVisibilityLoaded(event) {
    const { state } = event.detail
    this.visibilityState = { ...state }
    this.applySectionVisibility("appointments", state.appointments !== false)
    this.applySectionVisibility("sickNotes", state.sickNotes !== false)
    this.applySectionVisibility("leaveRequests", state.leaveRequests !== false)
    this.recalculateLayout()
  }

  // Apply visibility to a specific section type across all week rows
  applySectionVisibility(section, visible) {
    // Find all elements with matching section type in the month calendar
    const sectionElements = this.element.querySelectorAll(`[data-section-type="${section}"]`)

    sectionElements.forEach((element) => {
      element.classList.toggle("hidden", !visible)
    })
  }

  // Recalculate layout: adjust cell heights based on visible sections
  recalculateLayout() {
    if (!this.hasWeekContainerTarget) return

    this.weekContainerTargets.forEach((weekContainer) => {
      this.recalculateWeekLayout(weekContainer)
    })
  }

  // Recalculate layout for a single week container
  recalculateWeekLayout(weekContainer) {
    // Get heights for each section from data attributes
    const sectionHeights = {
      sickNotes: parseInt(weekContainer.dataset.sickNotesHeight || "0", 10),
      leaveRequests: parseInt(weekContainer.dataset.leaveRequestsHeight || "0", 10),
      appointments:
        parseInt(weekContainer.dataset.appointmentsHeight || "0", 10) +
        parseInt(weekContainer.dataset.birthdaysHeight || "0", 10),
      tasks: parseInt(weekContainer.dataset.tasksHeight || "0", 10),
    }

    const baseHeight = parseInt(weekContainer.dataset.baseHeight || "42", 10)
    const minHeight = parseInt(weekContainer.dataset.minHeight || "100", 10)

    // Calculate visible height
    let visibleHeight = sectionHeights.tasks // Tasks are always visible

    this.constructor.FILTERABLE_SECTIONS.forEach((section) => {
      const isVisible = this.visibilityState[section] !== false
      if (isVisible) {
        visibleHeight += sectionHeights[section]
      }
    })

    const newMinHeight = Math.max(minHeight, baseHeight + visibleHeight + 10)

    // Update day cell min-heights within this week
    const dayCells = weekContainer.querySelectorAll(
      '[data-plantafel-month-visibility-target="dayCell"]',
    )
    dayCells.forEach((cell) => {
      cell.style.minHeight = `${newMinHeight}px`
    })
  }
}
